import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertTriangle, Upload, X } from 'lucide-react';
import { openDispute, uploadShippingDocument } from '@/lib/api';
import { toast } from 'sonner';

const DISPUTE_TYPES = [
  { value: 'damage', label: 'Ware beschädigt' },
  { value: 'missing', label: 'Menge fehlt / unvollständig' },
  { value: 'wrong_item', label: 'Falscher Artikel geliefert' },
  { value: 'not_received', label: 'Ware nicht erhalten' },
  { value: 'quality', label: 'Qualität nicht wie beschrieben' },
  { value: 'other', label: 'Sonstiges' },
];

export const DisputeDialog = ({ open, onOpenChange, txn, onSuccess }) => {
  const [disputeType, setDisputeType] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceUrls, setEvidenceUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDisputeType('');
      setDescription('');
      setEvidenceUrls([]);
    }
  }, [open]);

  if (!txn) return null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadShippingDocument(file, txn.transaction_id);
      setEvidenceUrls((prev) => [...prev, { url: res.data.file_url, name: res.data.original_filename }]);
      toast.success('Foto/Dokument hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!disputeType) {
      toast.error('Bitte Reklamationstyp auswählen');
      return;
    }
    if (description.trim().length < 10) {
      toast.error('Bitte Beschreibung mit mind. 10 Zeichen angeben');
      return;
    }
    setSubmitting(true);
    try {
      await openDispute(txn.transaction_id, {
        dispute_type: disputeType,
        description: description.trim(),
        evidence_urls: evidenceUrls.map((e) => e.url),
      });
      toast.success('Reklamation eröffnet — Admin und Verkäufer wurden informiert');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reklamation konnte nicht eröffnet werden');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Reklamation eröffnen
          </DialogTitle>
          <DialogDescription>
            Beschreiben Sie das Problem mit Ihrer Lieferung. Ein Admin wird zwischen Ihnen und dem Verkäufer vermitteln.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium">{txn.material?.common_title || 'Artikel'}</p>
            <p className="text-muted-foreground text-xs">
              Bestell-Nr.: {txn.order_number || txn.transaction_id?.slice(0, 8)} · Verkäufer: {txn.seller_group?.group_name || '—'}
            </p>
          </div>

          <div>
            <Label className="text-sm">Art der Reklamation *</Label>
            <Select value={disputeType} onValueChange={setDisputeType}>
              <SelectTrigger className="mt-1" data-testid="dispute-type-select">
                <SelectValue placeholder="Bitte auswählen" />
              </SelectTrigger>
              <SelectContent>
                {DISPUTE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm">Beschreibung *</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Bitte beschreiben Sie das Problem so genau wie möglich (mind. 10 Zeichen) …"
              className="mt-1"
              data-testid="dispute-description-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">{description.length}/2000 Zeichen</p>
          </div>

          <div>
            <Label className="text-sm">Belege (Fotos/PDFs, optional)</Label>
            <div className="mt-1">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                id="dispute-evidence-upload"
                className="hidden"
                onChange={handleFile}
                data-testid="dispute-evidence-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('dispute-evidence-upload').click()}
                disabled={uploading}
                data-testid="dispute-evidence-upload-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Beleg hinzufügen
              </Button>
              {evidenceUrls.length > 0 && (
                <div className="mt-2 space-y-1">
                  {evidenceUrls.map((e, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1">
                      <a href={e.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate flex-1">
                        {e.name}
                      </a>
                      <button
                        type="button"
                        onClick={() => setEvidenceUrls((prev) => prev.filter((_, i) => i !== idx))}
                        className="ml-2 text-muted-foreground hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={handleSubmit}
            disabled={submitting || !disputeType || description.trim().length < 10}
            data-testid="dispute-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Reklamation einreichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
